import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { getMarketByIP } from '../../../actions/location-actions';
import { GET_ALL_PROMOS, GET_PROMOS_BY_BRANCH } from '../../../enums/graphql';
import { Button, Carousel, Container, Icon, classNames } from '@pella/sash-framework';
import { isValidArray } from '../../utilities/validations';
import { useMediaQuery } from 'react-responsive';
import BranchDisclaimerModal from '../branch-offer/branch-disclaimer-modal';
import { useBranchContext } from '../../../context/BranchContext';
import PropTypes from 'prop-types';

const PromoBanner = ({ type, isCollapsible, bgColor, classes = '', hasHeader = true, placement, staticPromos, badgeText = 'In-Home Consultation Promotions', stacked, showNationalPromo }) => {
	const isXSmallDevice = useMediaQuery({
		query: 'only screen and (max-width : 549px)',
	});
	const isSmallDevice = useMediaQuery({
		query: 'only screen and (min-width : 550px) and (max-width : 767px)',
	});
	const isMediumDevice = useMediaQuery({
		query: 'only screen and (min-width : 768px)',
	});
	const isXlargeDevice = useMediaQuery({
		query: 'only screen and (min-width : 1280px)',
	});

	const { currentBranch } = useBranchContext();
	const dispatch = useDispatch();
	const locations = useSelector((state) => state.locations);
	const [branchID, setBranchID] = useState(currentBranch?.branch_retail?.[0] || null);
	const [promoArray, setPromoArray] = useState(null);
	const [hasPromos, setHasPromos] = useState(null);
	const [hasStaticPromo, setHasStaticPromo] = useState(false);
	const [getPromoByBranchId, { data, loading }] = useLazyQuery(GET_PROMOS_BY_BRANCH, { variables: { branchIDs: branchID } });
	const [getAllPromos, { data: allPromoData, called, loading: allPromoLoading }] = useLazyQuery(GET_ALL_PROMOS);
	const promoData = data?.getPromotionsByBranchId;
	const [isMobilePromoOpen, setIsMobilePromoOpen] = useState(false);
	const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
	const slideCount = { xs: 1 };

	useEffect(() => {
		if (showNationalPromo) {
			getAllPromos();
		} else {
			if (!branchID) return;
			getPromoByBranchId();
		}
	}, [showNationalPromo, getAllPromos, getPromoByBranchId, branchID]);

	useEffect(() => {
		let params = new URLSearchParams(window.location.search);
		let branch = params.get('branch');

		if (branch) {
			setBranchID(branch);
		} else if (isValidArray(currentBranch?.branch_retail)) {
			setBranchID(currentBranch.branch_retail[0]);
		} else if (locations?.market?.retail_branch_id) {
			setBranchID(locations.market.retail_branch_id);
		} else {
			dispatch(getMarketByIP());
		}
	}, [locations?.market?.retail_branch_id, currentBranch, dispatch]);

	useEffect(() => {
		const queryString = window.location.search;
		if (queryString) {
			const utm = queryString.split('?')[1];
			if (utm && isValidArray(staticPromos)) {
				const matchingPromo = staticPromos.find((promo) => promo.utm?.some((promoUtm) => promoUtm === utm));

				if (matchingPromo) {
					setPromoArray([
						{
							promo: matchingPromo.term[0],
							disclaimer: matchingPromo.disclaimer,
							conjunction: matchingPromo.conjunction,
							promoBadgeText: matchingPromo.promo_text || badgeText,
						},
					]);
					setHasPromos(true);
					setHasStaticPromo(true);
					return;
				}
			}
		}

		if (!hasStaticPromo && isValidArray(promoData)) {
			let convertedPromos = convertPromotionsToArray(promoData);

			convertedPromos = convertedPromos.map((promo) => ({
				...promo,
				promoBadgeText: badgeText,
			}));

			setPromoArray(convertedPromos);
			setHasPromos(true);
		} else if (!hasStaticPromo && !loading) {
			setHasPromos(false);
		}
	}, [staticPromos, loading, promoData, hasStaticPromo, badgeText]);

	useEffect(() => {
		if (!showNationalPromo || !called || allPromoLoading) return;

		if (allPromoData?.getPromotions?.find((promo) => promo.national)) {
			const promo = allPromoData.getPromotions.filter((promo) => promo.national);
			let convertedPromos = convertPromotionsToArray(promo);
			convertedPromos = convertedPromos.map((promo) => ({
				...promo,
				promoBadgeText: badgeText,
			}));
			setPromoArray(convertedPromos);
			setHasPromos(true);
		} else {
			getPromoByBranchId();
		}
	}, [showNationalPromo, allPromoData, called, allPromoLoading, getPromoByBranchId, badgeText]);

	const convertPromotionsToArray = (data) => {
		const promos = data[0];
		const result = [];

		const pushPromo = (option, disclaimer, conjunction) => {
			if (option != null) {
				result.push({ promo: option, disclaimer });
				if (conjunction != null) {
					result[result.length - 1].conjunction = conjunction;
				}
			}
		};

		pushPromo(promos.option1, promos.disclaimer1, promos.conjunction1);
		pushPromo(promos.option2, promos.disclaimer2, promos.conjunction2);
		pushPromo(promos.option3, promos.disclaimer3);

		return result;
	};

	const renderDisclaimerNumber = (value) => {
		const { disclaimer1, disclaimer2, disclaimer3 } = promoData?.[0] || {};

		if (value === '2' && disclaimer1 === disclaimer2) {
			return '1';
		}

		if (value === '3') {
			if (disclaimer1 === disclaimer3) {
				return '1';
			}
			if (disclaimer2 === disclaimer3) {
				return '2';
			}
		}

		return value;
	};

	if (hasPromos === false) {
		//promo should still show if hasPromos is null
		return null;
	}

	const onMobilePromoClick = () => setIsMobilePromoOpen(!isMobilePromoOpen);
	const onDisclaimerModalClick = () => setIsDisclaimerModalOpen(!isDisclaimerModalOpen);

	const isSmallerThanMediumDevice = isSmallDevice || isXSmallDevice;

	const headerPromo = type === 'header';
	const branchShowroom = placement === 'branch-showroom';

	const forComponent = (!headerPromo && !isXlargeDevice && isValidArray(promoArray) && promoArray.length > 2) || hasStaticPromo;
	const forHeader = (headerPromo && !isSmallDevice && !isXSmallDevice && isValidArray(promoArray) && promoArray.length >= 2) || hasStaticPromo;
	const forXSmall = (isXSmallDevice && isValidArray(promoArray) && promoArray.length > 2) || hasStaticPromo;
	const branchShowroomException = branchShowroom && isMediumDevice;
	const renderPromoCarousel = (forComponent && !branchShowroomException) || forHeader || forXSmall;

	const gridColumns =
		isValidArray(promoArray) &&
		promoArray
			.map((promo, index) => {
				const columnWidth = promoArray.length === 1 ? '1fr' : headerPromo || (!headerPromo && isXlargeDevice) ? '345px' : '240px';
				const column = promo.conjunction ? `minmax(141px, ${columnWidth}) max-content` : `minmax(141px, ${columnWidth})`;
				return index === promoArray.length - 1 ? column : `${column} `;
			})
			.join('');

	const renderPromoBadge = (promoBadgeText) => (
		<div className='text-body-2 flex w-fit items-center gap-x-0.5 border border-neutral-100 bg-white px-2 py-0.5 font-semibold max-xl:mx-auto max-xl:mb-1.5 xl:mr-6'>
			<Icon
				name='sell'
				size={14}
				weight={700}
			/>
			<p>{promoBadgeText || badgeText}</p>
		</div>
	);

	const renderPromos = (promoArray) => {
		const promoItem = classNames({
			'grid grid-rows-[minmax(0,_1fr)_max-content] py-2': true,
			'@3xl/promo-banner:flex @3xl/promo-banner:items-center @3xl/promo-banner:justify-center @3xl/promo-banner:pt-1.5 @3xl/promo-banner:pb-0 @7xl/promo-banner:pt-0': headerPromo,
			'items-end': !headerPromo,
		});

		const promoItemTextContainer = classNames({
			'grid place-items-center bg-black text-center p-4': true,
			'h-24 @3xl/promo-banner:mr-6 @3xl/promo-banner:h-fit @3xl/promo-banner:bg-transparent @3xl/promo-banner:p-0 @3xl/promo-banner:flex': headerPromo,
			'h-32 md:h-[108px] xl:h-36': !headerPromo,
			'!md:h-24': branchShowroom,
		});

		const promoItemText = classNames({
			'text-caption text-white [&_span]:mb-2 [&_span]:block [&_span]:text-xl [&_span]:font-bold [&_span]:text-primary @3xl/promo-banner:tracking-normal tracking-tight': true,
			'md:text-body-2 md:font-semibold md:text-black md:[&_span]:text-black md:[&_span]:mb-0 md:[&_span]:!text-body-2 md:[&_span]:font-semibold md:[&_span]:inline': headerPromo,
		});

		const promoDisclaimerButton = classNames({
			'!decoration-1 !text-caption capitalize !text-neutral-600 !decoration-neutral-600': true,
			'!bg-white': bgColor,
			'pt-0 pb-0.5': !headerPromo,
		});

		const promoConjuction = classNames({
			'grid grid-rows-[minmax(0,_1fr)_max-content] py-2': true,
		});

		const promoConjuctionText = classNames({
			'text-overline grid h-24 place-items-center text-center': true,
			'h-32 md:h-[108px] xl:h-36': !headerPromo && !stacked,
			'!md:h-24': branchShowroom,
			'h-auto [&+div]:hidden': stacked,
		});

		return promoArray.map((promo, index) => {
			const { promo: promoText, disclaimer, conjunction } = promo || {};
			const key = `promo-${index}`;

			return (
				<React.Fragment key={key}>
					<div className={promoItem}>
						<div className={promoItemTextContainer}>
							<p className={promoItemText}>
								{promoText}
								<sup>{renderDisclaimerNumber(index + 1)}</sup>
							</p>
						</div>
						{disclaimer && (
							<Button
								className={promoDisclaimerButton}
								color='tertiary'
								onClick={onDisclaimerModalClick}
							>
								Details
							</Button>
						)}
					</div>
					{conjunction && (
						<div className={promoConjuction}>
							<p className={promoConjuctionText}>{conjunction}</p>
							<div>&nbsp;</div>
						</div>
					)}
				</React.Fragment>
			);
		});
	};

	const renderCarouselPromos = (promoArray) => {
		if (!promoArray) {
			return null;
		}
		return promoArray.map((promo, index) => {
			const { promo: promoText, disclaimer, conjunction, promoBadgeText } = promo || {};
			const key = `promo-${index}`;

			const carouselPromoItem = classNames({
				'grid gap-x-1.5 pt-2': true,
				'grid-cols-[minmax(141px,_141px)]': !headerPromo || isXSmallDevice,
				'grid-cols-[minmax(141px,_345px)]': headerPromo && !isXSmallDevice,
				'max-content': conjunction && (!headerPromo || isXSmallDevice),
				'@3xl/promo-banner:flex @3xl/promo-banner:items-center @3xl/promo-banner:justify-center @7xl/promo-banner:pt-0': headerPromo,
			});

			const carouselPromo = classNames({
				'grid place-items-center bg-black text-center p-4': true,
				'h-24 @3xl/promo-banner:bg-transparent @3xl/promo-banner:h-fit @3xl/promo-banner:mr-3 @3xl/promo-banner:p-0': headerPromo,
				'h-32 md:h-[108px] xl:h-36': !headerPromo && !branchShowroom,
				'!md:h-24': branchShowroom,
			});

			const carouselPromoText = classNames({
				'text-caption text-white [&_span]:mb-2 [&_span]:block [&_span]:text-xl [&_span]:font-bold [&_span]:text-primary @3xl/promo-banner:tracking-normal tracking-tight': true,
				'md:text-body-2 md:font-semibold md:text-black md:[&_span]:text-black md:[&_span]:mb-0 md:[&_span]:!text-body-2 md:[&_span]:font-semibold md:[&_span]:inline': headerPromo,
			});

			const carouselDisclaimerButton = classNames({
				'!text-caption capitalize !text-neutral-600 !decoration-neutral-600 !decoration-1 underline-offset-auto': true,
				'!bg-white': bgColor,
				'@3xl/promo-banner:font-normal @3xl/promo-banner:p-0 @3xl/promo-banner:order-3 @3xl/promo-banner:!text-body-2': headerPromo,
			});

			const carouselConjuction = classNames({
				'col-start-2 row-start-1 grid': true,
				'@3xl/promo-banner:mr-6': headerPromo,
			});

			const carouselConjuctionText = classNames({
				'text-overline grid place-items-center text-center': true,
				'h-24 @3xl/promo-banner:h-fit': headerPromo,
				'h-32 md:h-[108px] xl:h-36': !headerPromo && !branchShowroom,
				'!md:h-24': branchShowroom,
			});

			return (
				<div
					className={carouselPromoItem}
					key={key}
				>
					{isXlargeDevice && type === 'header' && isValidArray(promoArray) && renderPromoBadge(promoBadgeText)}
					<div className={carouselPromo}>
						<p className={carouselPromoText}>
							{promoText}
							<sup>{renderDisclaimerNumber(index + 1)}</sup>
						</p>
					</div>
					{disclaimer && (
						<Button
							className={carouselDisclaimerButton}
							color='tertiary'
							onClick={onDisclaimerModalClick}
						>
							Details
						</Button>
					)}
					{conjunction && (
						<div className={carouselConjuction}>
							<p className={carouselConjuctionText}>{conjunction}</p>
						</div>
					)}
				</div>
			);
		});
	};

	const promoToggle = (
		<div
			className='bg-primary'
			id='promo-toggle'
		>
			<Container className='!px-4'>
				<div className='text-overline bg-primary flex items-center gap-x-1.5 py-2 text-neutral-900'>
					<Icon
						name='sell'
						size={14}
						weight={700}
					/>
					<p>{isValidArray(promoArray) ? promoArray[0]?.promoBadgeText : badgeText}</p>
					{isCollapsible && (
						<Button
							className='ml-auto !w-fit'
							color='tertiary-none'
							onClick={() => onMobilePromoClick()}
						>
							<Icon
								name='keyboard_arrow_down'
								className={`${isMobilePromoOpen ? 'rotate-180' : ''} transform`}
							/>
						</Button>
					)}
				</div>
			</Container>
		</div>
	);

	const promoBanner = classNames({
		[bgColor]: bgColor,
		'bg-white': !bgColor,
		'@3xl/promo-banner:bg-primary bg-white': headerPromo,
	});

	const promoBannerContainer = classNames({
		'!px-0': renderPromoCarousel || !headerPromo,
		'@3xl/promo-banner:pt-2 @7xl/promo-banner:flex @7xl/promo-banner:items-center @7xl/promo-banner:justify-center @7xl/promo-banner:pb-1 @7xl/promo-banner:pt-3': headerPromo,
	});

	const promoCarousel = classNames({
		'!gap-x-1.5 before:content-none after:content-none [&_li]:flex-none': true,
		'@3xl/promo-banner:[&_li]:w-full': headerPromo,
	});

	const promoCarouselWrapper = classNames({
		'[&>ul]:mt-0': true,
		'max-md:mb-5': headerPromo,
	});

	const promoNonCarousel = classNames({
		'grid gap-x-1.5': true,
		'justify-center': headerPromo,
		'justify-start': !headerPromo,
		'grid-cols-1 auto-rows-min': stacked,
	});

	return (
		<div className={`${classes} @container/promo-banner`}>
			{hasHeader && type === 'component' && promoToggle}

			<div
				className={promoBanner}
				id='promo-banner'
			>
				<Container className={promoBannerContainer}>
					{type === 'header' && !isSmallerThanMediumDevice && !isXlargeDevice && renderPromoBadge(isValidArray(promoArray) ? promoArray[0]?.promoBadgeText : badgeText)}
					{(!isCollapsible || (isSmallerThanMediumDevice && isMobilePromoOpen) || !isSmallerThanMediumDevice) && (
						<>
							{renderPromoCarousel ? (
								<Carousel
									allowOverflow={isXSmallDevice ? true : false}
									slidesToShow={slideCount}
									showDots
									className={promoCarousel}
									wrapperClassName={promoCarouselWrapper}
								>
									{renderCarouselPromos(promoArray, bgColor)}
								</Carousel>
							) : (
								isValidArray(promoArray) && (
									<div
										style={!stacked ? { gridTemplateColumns: gridColumns } : null}
										className={promoNonCarousel}
									>
										{renderPromos(promoArray, bgColor)}
									</div>
								)
							)}
						</>
					)}
				</Container>
			</div>

			{hasHeader && isSmallerThanMediumDevice && type === 'header' && promoToggle}

			<BranchDisclaimerModal
				isOpen={isDisclaimerModalOpen}
				onClose={() => onDisclaimerModalClick()}
				promoArray={promoArray}
			/>
		</div>
	);
};

export default PromoBanner;

PromoBanner.propTypes = {
	type: PropTypes.string,
	isCollapsible: PropTypes.bool,
	bgColor: PropTypes.string,
	classes: PropTypes.string,
	hasHeader: PropTypes.bool,
	placement: PropTypes.string,
	staticPromos: PropTypes.array,
	badgeText: PropTypes.string,
	stacked: PropTypes.bool,
	showNationalPromo: PropTypes.bool,
};
